import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Calendar, DollarSign, Activity, Package, Car, Wrench, Droplet, Wind, Shield, CheckCircle, Cog, Disc } from 'lucide-react';

const getServiceIcon = (type, size = 18, color = 'var(--text-secondary)') => {
    if (!type) return <Package size={size} color={color} />;
    const t = type.toLowerCase();
    if (t.includes('oil')) return <Droplet size={size} color={color} />;
    if (t.includes('fluid')) return <Droplet size={size} color={color} />;
    if (t.includes('brake')) return <Disc size={size} color={color} />;
    if (t.includes('filter')) return <Wind size={size} color={color} />;
    if (t.includes('suspension')) return <Wrench size={size} color={color} />;
    if (t.includes('inspection')) return <CheckCircle size={size} color={color} />;
    if (t.includes('repair') || t.includes('maintenance')) return <Cog size={size} color={color} />;
    return <Package size={size} color={color} />;
};

const VIEWS = ['pie', 'bar'];
const COLORS = ['#738ADB', '#22c55e', '#ef4444', '#f59e0b', '#ec4899', '#4f46e5', '#14b8a6', '#8b5cf6'];

const ServiceStatistics = ({ user, language = 'en' }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewIndex, setViewIndex] = useState(0);
    const [revenueView, setRevenueView] = useState('monthly');
    const [carSortView, setCarSortView] = useState('repairs');
    const currentView = VIEWS[viewIndex];

    useEffect(() => {
        if (!user) return;
        const fetchLogs = async () => {
            setLoading(true);
            const { data } = await supabase
                .from('service_maintenance_logs')
                .select('service_type, cost, service_date, car_id, cars(name, plate_number)')
                .eq('service_user_id', user.id)
                .order('service_date', { ascending: true });
            
            setLogs(data || []);
            setLoading(false);
        };
        fetchLogs();
    }, [user]);

    // Data grouped by Service Type
    const typeData = useMemo(() => {
        const aggregated = logs.reduce((acc, curr) => {
            const amt = parseFloat(curr.cost) || 0;
            if (amt === 0) return acc;
            
            const typeName = curr.service_type || 'Other';
            const found = acc.find(item => item.name === typeName);
            if (found) {
                found.value += amt;
            } else {
                acc.push({ name: typeName, value: amt });
            }
            return acc;
        }, []);
        // Sort descending
        return aggregated.sort((a, b) => b.value - a.value);
    }, [logs]);

    const carData = useMemo(() => {
        const aggregated = logs.reduce((acc, curr) => {
            if (!curr.car_id || !curr.cars) return acc;
            const carName = curr.cars.name || curr.cars.plate_number || 'Unknown Car';
            const amt = parseFloat(curr.cost) || 0;
            const found = acc.find(item => item.id === curr.car_id);
            if (found) {
                found.cost += amt;
                found.repairs += 1;
            } else {
                acc.push({ id: curr.car_id, name: carName, cost: amt, repairs: 1 });
            }
            return acc;
        }, []);
        return aggregated;
    }, [logs]);

    const topCarsByRepairs = useMemo(() => [...carData].sort((a, b) => b.repairs - a.repairs), [carData]);
    const topCarsByCost = useMemo(() => [...carData].sort((a, b) => b.cost - a.cost), [carData]);

    const totalRevenue = useMemo(() => {
        return logs.reduce((sum, log) => sum + (parseFloat(log.cost) || 0), 0);
    }, [logs]);

    const currentMonthRevenue = useMemo(() => {
        const now = new Date();
        const currentMonthKey = now.toISOString().slice(0, 7); // YYYY-MM
        return logs.reduce((sum, log) => {
            if (log.service_date && log.service_date.slice(0, 7) === currentMonthKey) {
                return sum + (parseFloat(log.cost) || 0);
            }
            return sum;
        }, 0);
    }, [logs]);

    const cycleView = () => setViewIndex(i => (i + 1) % VIEWS.length);

    const viewIcon = currentView === 'pie' ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" /></svg>
    ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.21 15.89A10 10 0 1 1 8 2.83" /><path d="M22 12A10 10 0 0 0 12 2v10z" /></svg>
    );

    const tooltipStyle = {
        contentStyle: {
            backgroundColor: 'var(--bg-secondary)',
            borderColor: 'var(--border-color)',
            borderRadius: '12px',
            fontSize: '0.85rem',
            color: 'var(--text-primary)',
        },
        itemStyle: { color: 'var(--text-primary)' },
    };

    const renderCustomBarLabel = (props) => {
        const { x, y, width, height, index } = props;
        const barData = typeData[index];
        if (!barData) return null;
        const iconSize = 18;
        const cx = x + width / 2;
        const fitsInside = height > 28;
        const cy = fitsInside ? y + height - 28 : y - 24;
        const color = fitsInside ? '#ffffff' : 'var(--text-secondary)';
        
        return (
            <g transform={`translate(${cx - iconSize / 2}, ${cy})`}>
                {getServiceIcon(barData.name, iconSize, color)}
            </g>
        );
    };

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>Loading statistics...</div>;
    }

    if (logs.length === 0 || totalRevenue === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-secondary)' }}>
                <Activity size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                <p style={{ margin: '0 0 0.5rem', fontWeight: 600, fontSize: '1.1rem' }}>No Revenue Data</p>
                <p style={{ margin: 0, fontSize: '0.9rem' }}>Add priced maintenance records to see your revenue stats.</p>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', padding: '0.5rem 0' }}>
            {/* Header Totals */}
            <div 
                onClick={() => setRevenueView(prev => prev === 'monthly' ? 'all-time' : 'monthly')}
                style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    borderRadius: '16px', padding: '1.5rem', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    boxShadow: '0 8px 16px -4px rgba(16,185,129,0.35)',
                    cursor: 'pointer', transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}
                onMouseOver={e => e.currentTarget.style.transform = 'scale(1.02)'}
                onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
            >
                <div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, opacity: 0.9, marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {revenueView === 'monthly' ? 'Monthly Revenue' : 'All-Time Revenue'}
                    </div>
                    <div style={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1 }}>
                        ${(revenueView === 'monthly' ? currentMonthRevenue : totalRevenue).toFixed(2)}
                    </div>
                </div>
                <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <DollarSign size={28} />
                </div>
            </div>

            {/* Revenue By Type */}
            <div style={{ background: 'var(--bg-secondary)', borderRadius: '16px', padding: '1.25rem', border: '1px solid var(--border-color)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Package size={18} /> Revenue by Service Type
                    </h3>
                    <button
                        onClick={cycleView}
                        title="Toggle View"
                        style={{
                            display: 'flex', alignItems: 'center', gap: '0.35rem',
                            padding: '0.35rem 0.75rem', borderRadius: '8px', border: '1px solid var(--border-color)',
                            background: 'var(--bg-primary)', color: 'var(--text-secondary)',
                            fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s'
                        }}
                    >
                        {viewIcon} {currentView === 'pie' ? 'Bar' : 'Pie'}
                    </button>
                </div>

                <div style={{ height: 300 }}>
                    {currentView === 'pie' ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={typeData}
                                    cx="50%" cy="50%"
                                    innerRadius={60} outerRadius={85}
                                    paddingAngle={3}
                                    dataKey="value"
                                    stroke="none"
                                >
                                    {typeData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip {...tooltipStyle} formatter={(val) => `$${val.toFixed(2)}`} />
                                <Legend wrapperStyle={{ fontSize: '0.85rem' }} iconType="circle" />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={typeData} margin={{ top: 25, right: 10, left: 0, bottom: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
                                <XAxis dataKey="name" tick={false} tickLine={false} axisLine={false} height={5} />
                                <YAxis 
                                    tick={{ fill: 'var(--text-secondary)', fontSize: '0.75rem' }} 
                                    tickLine={false} 
                                    axisLine={false} 
                                    tickFormatter={(val) => val >= 1000 ? `$${(val / 1000).toFixed(val % 1000 === 0 ? 0 : 1)}k` : `$${val}`} 
                                />
                                <Tooltip {...tooltipStyle} formatter={(val) => `$${val.toFixed(2)}`} cursor={{ fill: 'rgba(0,0,0,0.05)' }} />
                                <Bar dataKey="value" radius={[6, 6, 0, 0]} label={renderCustomBarLabel}>
                                    {typeData.map((entry, index) => (
                                        <Cell key={`bar-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            {/* Top Cars */}
            <div style={{ background: 'var(--bg-secondary)', borderRadius: '16px', padding: '1.25rem', border: '1px solid var(--border-color)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Car size={18} /> Top Cars
                    </h3>
                    <button
                        onClick={() => setCarSortView(v => v === 'repairs' ? 'cost' : 'repairs')}
                        title="Toggle View"
                        style={{
                            display: 'flex', alignItems: 'center', gap: '0.35rem',
                            padding: '0.35rem 0.75rem', borderRadius: '8px', border: '1px solid var(--border-color)',
                            background: 'var(--bg-primary)', color: 'var(--text-secondary)',
                            fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s'
                        }}
                    >
                        {carSortView === 'repairs' ? <Wrench size={14} /> : <DollarSign size={14} />}
                        {carSortView === 'repairs' ? 'Most Repairs' : 'Most Costly'}
                    </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {(carSortView === 'repairs' ? topCarsByRepairs : topCarsByCost).slice(0, 5).map((car, idx) => (
                        <div key={car.id || idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', background: 'var(--bg-primary)', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <div style={{ fontWeight: 600, color: 'var(--text-secondary)', width: '16px', textAlign: 'center' }}>
                                    {idx + 1}
                                </div>
                                <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                    {car.name}
                                </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ fontWeight: 700, color: carSortView === 'cost' ? '#10b981' : 'var(--text-primary)' }}>
                                    {carSortView === 'cost' ? `$${car.cost.toFixed(2)}` : `${car.repairs} repairs`}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                    {carSortView === 'cost' ? `${car.repairs} repairs` : `$${car.cost.toFixed(2)}`}
                                </div>
                            </div>
                        </div>
                    ))}
                    {(carSortView === 'repairs' ? topCarsByRepairs : topCarsByCost).length === 0 && (
                        <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-secondary)' }}>
                            No car data available.
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
};

export default ServiceStatistics;
